// src/components/EventForm/OptionsSection.js
import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';
import { useTranslation } from 'react-i18next';

import Tooltip from '../Tooltip/Tooltip';
import Typography from '../../components/Typography';
import OptionsIcon from '../../components/Icons/OptionsIcon';
import EventTypeIcon from '../../components/Icons/EventTypeIcon';
import TicketsIcon from '../../components/Icons/TicketsIcon';
import TerrainIcon from '../../components/Icons/TerrainIcon';
import ExperienceIcon from '../../components/Icons/ExperienceIcon';
import WheelIcon from '../../components/Icons/WheelIcon';
import EditIcon from '../../components/Icons/EditIcon';
import InfoCircleOutlineIcon from '../Icons/InfoCircleOutlineIcon';
import ModalityIcon from '../Icons/ModalityIcon';
import { TICKET_FREE, TERRAIN_TYPES } from '../../constants/eventTypes';
import { getVehicleTypesWithLabels, VEHICLE_TYPES } from '../../constants/vehicleTypes';

const OptionsSection = ({
  formData,
  isEditMode,
  handleOpenModal,
  savedOptions
}) => {
  const { t } = useTranslation(['createEvent', 'eventCategories']);

  const isActivityUpdated = !!savedOptions?.activity;
  const ticket = formData.tickets?.[0];
  const isTicketUpdated = !!savedOptions?.ticket;
  const isModalityUpdated = !!savedOptions?.modality;
  const isOrganizerVehicleUpdated = !!savedOptions?.organizerVehicle;
  const isTerrainChanged = Array.isArray(formData.terrain)
    ? formData.terrain.join(',') !== [TERRAIN_TYPES[0]].join(',')
    : formData.terrain !== TERRAIN_TYPES[0];
  const isTerrainUpdated = !!savedOptions?.terrain || isTerrainChanged;
  const isExperienceUpdated = !!savedOptions?.experience;
  const vehicleTypesWithLabels = getVehicleTypesWithLabels();
  const allVehicleTypeValues = vehicleTypesWithLabels.map((v) => v.value);
  const vehicleTypeLabels = vehicleTypesWithLabels.reduce((acc, cur) => {
    acc[cur.value] = cur.label;
    return acc;
  }, {});
  const allowedVehicleTypes = Array.isArray(formData.allowedVehicleTypes)
    ? formData.allowedVehicleTypes.filter((type) => allVehicleTypeValues.includes(type))
    : [];
  const allowedVehicleTypesEffective = allowedVehicleTypes.length > 0 ? allowedVehicleTypes : [VEHICLE_TYPES.MOTORCYCLE];
  const hasAllVehicleTypesSelected = formData.needsVehicle && allowedVehicleTypesEffective.length === allVehicleTypeValues.length;
  const modalityLabel = formData.needsVehicle
    ? (hasAllVehicleTypesSelected
      ? t('eventForm.options.allVehicles')
      : (() => {
        const ordered = allVehicleTypeValues.filter((type) => allowedVehicleTypesEffective.includes(type));
        const labels = ordered.map((type) => vehicleTypeLabels[type] || type);
        if (labels.length > 2) return `${labels[0]}, ${labels[1]} (+${labels.length - 2})`;
        return labels.join(', ');
      })())
    : t('createEventModalityModal.withoutVehicle');

  return (
    <Wrapper>
      <SectionHeader>
        <OptionsIcon stroke={theme.colors.defaultStrong} />
        <Typography as="label" $variant="body-1-semibold" color={theme.colors.defaultStrong}>
          {t('eventForm.options.heading')}
        </Typography>
        <Tooltip text={t('eventForm.options.tooltip')} maxWidth='260px' style={{ marginLeft: '-4px' }} position="top">
          <InfoButton>
            <InfoCircleOutlineIcon width="20px" height="20px" fill={theme.colors.defaultSubtle} />
          </InfoButton>
        </Tooltip>
      </SectionHeader>
      <OptionsContainer>
        <Option onClick={() => handleOpenModal('eventType')}>
          <OptionTitle>
            <EventTypeIcon fill={theme.colors.defaultStrong} />
            <Typography as="p" $variant="body-1-medium" color={theme.colors.defaultStrong}>
              {t('eventForm.options.eventType.label')}
            </Typography>
          </OptionTitle>
          <OptionSelected>
            <Typography as="p" $variant="body-1-medium" color={isActivityUpdated ? theme.colors.brandMain : theme.colors.defaultSubtle}>
              {t(`eventCategories:type.${formData.eventType}`)}
            </Typography>
            <EditIcon fill={isActivityUpdated ? theme.colors.brandMain : theme.colors.defaultSubtle} />
          </OptionSelected>
        </Option>

        {/* Ticket */}
        {!isEditMode && (
          <Option onClick={() => handleOpenModal('ticket')}>
            <OptionTitle>
              <TicketsIcon fill={theme.colors.defaultStrong} />
              <Typography as="p" $variant="body-1-medium" color={theme.colors.defaultStrong}>
                {t('eventForm.options.ticket.label')}
              </Typography>
            </OptionTitle>
            <OptionSelected>
              <Typography as="p" $variant="body-1-medium" color={isTicketUpdated ? theme.colors.brandMain : theme.colors.defaultSubtle}>
                {(ticket?.type || TICKET_FREE) === TICKET_FREE
                  ? (ticket?.approvalRequired
                    ? t('ticket.freeManual', { ns: 'eventCategories' })
                    : t('ticket.freeAuto', { ns: 'eventCategories' }))
                  : t('ticket.paidWithPrice', {
                    ns: 'eventCategories',
                    price: `${Number(ticket?.price) || 0}€`,
                  })}
              </Typography>
              <EditIcon fill={isTicketUpdated ? theme.colors.brandMain : theme.colors.defaultSubtle} />
            </OptionSelected>
          </Option>
        )}

        <Option onClick={() => handleOpenModal('vehicleRequirement')}>
          <OptionTitle>
            <ModalityIcon fill={theme.colors.defaultStrong} />
            <Typography as="p" $variant="body-1-medium" color={theme.colors.defaultStrong}>
              {t('eventForm.options.vehicle.label')}
            </Typography>
          </OptionTitle>
          <OptionSelected>
            <Typography as="p" $variant="body-1-medium" color={isModalityUpdated ? theme.colors.brandMain : theme.colors.defaultSubtle}>
              {modalityLabel}
            </Typography>
            <EditIcon fill={isModalityUpdated ? theme.colors.brandMain : theme.colors.defaultSubtle} />
          </OptionSelected>
        </Option>
        {formData.needsVehicle && (
          <>
            <Option onClick={() => handleOpenModal('selectVehicle')}>
              <OptionTitle>
                <WheelIcon fill={theme.colors.defaultStrong} />
                <Typography as="p" $variant="body-1-medium" color={theme.colors.defaultStrong}>
                  {t('eventForm.options.organizerVehicle.label')}
                </Typography>
              </OptionTitle>
              <OptionSelected>
                <Typography as="p" $variant="body-1-medium" color={isOrganizerVehicleUpdated && formData.organizerVehicle ? theme.colors.brandMain : theme.colors.defaultSubtle}>
                  {formData.organizerVehicle
                    ? formData.organizerVehicle.nickname || formData.organizerVehicle.model
                    : t('eventForm.options.organizerVehicle.select')}
                </Typography>
                <EditIcon fill={isOrganizerVehicleUpdated && formData.organizerVehicle ? theme.colors.brandMain : theme.colors.defaultSubtle} />
              </OptionSelected>
            </Option>
            {/* Terrain */}
            <Option onClick={() => handleOpenModal('terrain')}>
              <OptionTitle>
                <TerrainIcon fill={theme.colors.defaultStrong} />
                <Typography as="p" $variant="body-1-medium" color={theme.colors.defaultStrong}>
                  {t('eventForm.options.terrain.label')}
                </Typography>
              </OptionTitle>
              <OptionSelected>
                <Typography as="p" $variant="body-1-medium" color={isTerrainUpdated ? theme.colors.brandMain : theme.colors.defaultSubtle}>
                  {formData.terrain
                    ? (Array.isArray(formData.terrain)
                      ? formData.terrain.map(terrain => t(`eventCategories:terrain.${terrain}`)).join(', ')
                      : t(`eventCategories:terrain.${formData.terrain}`))
                    : t('eventForm.options.select')}
                </Typography>
                <EditIcon fill={isTerrainUpdated ? theme.colors.brandMain : theme.colors.defaultSubtle} />
              </OptionSelected>
            </Option>

            {/* <Option onClick={() => handleOpenModal('terrain')}>
              <OptionTitle>
                <TerrainIcon fill={theme.colors.defaultStrong} />
                <Typography as="p" $variant="body-1-medium" color={theme.colors.defaultStrong}>
                  {t('eventForm.options.terrain.label')}
                </Typography>
              </OptionTitle>
              <OptionSelected>
                <Typography as="p" $variant="body-1-medium" color={theme.colors.defaultSubtle}>
                  {t(`eventCategories:terrain.${formData.terrain}`)}
                </Typography>
                <EditIcon fill={theme.colors.defaultSubtle} />
              </OptionSelected>
            </Option> */}

            <Option onClick={() => handleOpenModal('experience')}>
              <OptionTitle>
                <ExperienceIcon fill={theme.colors.defaultStrong} />
                <Typography as="p" $variant="body-1-medium" color={theme.colors.defaultStrong}>
                  {t('eventForm.options.experience.label')}
                </Typography>
              </OptionTitle>
              <OptionSelected>
                <Typography as="p" $variant="body-1-medium" color={isExperienceUpdated ? theme.colors.brandMain : theme.colors.defaultSubtle}>
                  {t(`eventCategories:experience.${formData.experience}`)}
                </Typography>
                <EditIcon fill={isExperienceUpdated ? theme.colors.brandMain : theme.colors.defaultSubtle} />
              </OptionSelected>
            </Option>
          </>
        )}
      </OptionsContainer>
    </Wrapper>
  );
};

export default OptionsSection;

const Wrapper = styled.div`
  align-items: stretch;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm} 0;
`;

const SectionHeader = styled.div`
  align-items: center;
  display: inline-flex;
  gap: ${({ theme }) => theme.sizing.xs};;
`;

const OptionsContainer = styled.div`
  background: ${({ theme }) => theme.fill.defaultSubtle};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.sizing.xs};
  overflow: hidden;
`;

const Option = styled.div`
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.border.defaultWeak};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: 12px ${({ theme }) => theme.sizing.sm};
  transition: background 0.3s;

  &:first-child {
    border-top: none;
  }

  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
  }
`;

const OptionTitle = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const OptionSelected = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const InfoButton = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: ${({ theme }) => theme.sizing.md};
  justify-content: center;
  transition: background-color 0.3s ease-in-out;
  width: ${({ theme }) => theme.sizing.md};

  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
  }
`;
